import { css } from '@emotion/react';
import t from 'react-translate';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import store, { useAppDispatch } from 'redux/store';
import { Button } from 'react-bootstrap';
import { AngularServicesContext } from 'react-app';

// redux
import { addAlertMessage, removeAlertMessage } from 'redux/actions/alert-messages';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { copyLectureComponent, moveLectureComponent } from 'redux/actions/lecture-components';
import { getCourseLecturePageTimeline } from 'redux/actions/timeline';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { LectureComponent } from 'redux/schemas/models/lecture-component';
import { getCurrentUserId } from 'redux/selectors/users';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { getLecturePage } from 'redux/selectors/timeline';

import {
  standardSpacing,
  halfSpacing,
  threeQuartersSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import { black, gray7 } from 'styles/global_defaults/colors';
import NvIcon from 'shared/components/nv-icon';
import { useLecturePageLink } from 'lecture_pages/hooks/lecture-routing';
import ClickableContainer from 'components/clickable-container';
import { LecturePageMode } from 'lecture_pages/components';
import { loadLecturePageModel } from 'lecture_pages/components/content-area/angular-lecture-component';
import { getLectureComponentDomId } from 'lecture_pages/directives/components/base-lecture-component';
import getComponentMetadata from 'lecture_pages/components/data';
import { LecturePagePreviewContext, PreviewSource } from 'lecture_pages/components/lecture-page-preview-modal';

const styles = css`
    height: ${standardSpacing}px;
    max-width: 800px;

    .container-bar {
      display: flex;
      opacity: 0;
      cursor: pointer;
    }

    .copy-bar {
      cursor: pointer;
    }

    &:focus-within .container-bar,
    &:hover .container-bar {
      opacity: 1;
      cursor: pointer;
      background-color: ${gray7};
      justify-content: center;
      height: 100%;
      padding-left: ${halfSpacing}px;
      .copy-bar {
        display: flex;
      }
    }
`;

const alertMessageExtraStyle = css`
  .flash-close {
    .icon-close {
      color: ${black} !important;
      font-size: 16px;
    }
  }
  .flash-content {
    justify-content: normal;
    padding: ${threeQuartersSpacing}px ${tripleSpacing}px !important;
    .flash-button {
      width: 30%;
      display: flex;
      justify-content: end !important;
    }
  }
`;

type NvCopyComponentProps = {
  index: number
  catalogId: string
  currentLecturePageId: number
};
/**
 * Copy/Move functionalities are performed here
 * @param index copying/moving location,
 * @param catalogId
 * @param currentLecturePageId
 * @returns Redirects to the destination after copy/move action
 */
export const NvCopyComponent = (props: NvCopyComponentProps) => {
  const { component } = useSelector(
    state => state.app.destinationLecturePage,
  );
  const userId = useSelector(state => getCurrentUserId(state));
  const courseAliases = useSelector(state => getFlatCourseAliases(state, props.catalogId));
  const { isContentManagementCollection } = useSelector(getCurrentCourse);

  const { $injector, $scope } = useContext(AngularServicesContext);
  const angularComponent = $scope[`lectureComponent${component?.id}`];

  const { lecturePageId, onClosePreviewModal, source, onAction } = useContext(LecturePagePreviewContext);
  const lecturePage = useSelector((state) => getLecturePage(state, lecturePageId));

  const [isRequesting, setIsRequesting] = useState(false);

  const lectureLink = useLecturePageLink();
  const dispatch = useAppDispatch();

  const metadata = useMemo(() => getComponentMetadata(component?.trueType), [component?.trueType]);

  const redirectButton = (lectureActivityId: number) => (
    <Button
      type='primary'
      onClick={() => {
        lectureLink({
          catalogId: props.catalogId,
          lecturePageId,
          mode: LecturePageMode.EDIT,
          lectureActivityId,
        });
        dispatch(removeAlertMessage());
      }}
    >
      {t.LECTURE_PAGES.COMPONENTS.TAKE_ME_THERE()}
    </Button>
  );

  const onSelect = useCallback(() => {
    setIsRequesting(true);
    let actionTo: typeof copyLectureComponent | typeof moveLectureComponent = null;

    if (source === PreviewSource.COPY) {
      actionTo = copyLectureComponent;
    } else if (source === PreviewSource.MOVE) {
      actionTo = moveLectureComponent;
    }

    const getSuccessMessage = () => {
      switch (source) {
        case PreviewSource.COPY:
          return t.LECTURE_PAGES.COMPONENTS.COPY_TO_SUCCESS_MESSAGE(lecturePage?.title);
        case PreviewSource.MOVE:
          return t.LECTURE_PAGES.COMPONENTS.MOVE_TO_SUCCESS_MESSAGE(lecturePage?.title);
        default:
          return null;
      }
    };

    const doActionTo = () => {
      if (onAction) {
        onAction(props.index);
        return;
      }

      dispatch(actionTo({
        // For lesson library, catalogId is not used in copyTo and moveTo API
        catalogId: !isContentManagementCollection ? props.catalogId : null,
        lecturePageId: props.currentLecturePageId,
        lectureComponent: component,
        toLecturePageId: lecturePageId.toString(),
        newDestinationIndex: props.index.toString(),
      })).then(result => {
        setIsRequesting(false);
        onClosePreviewModal?.();
        if (actionTo.rejected.match(result)) {
          dispatch(addAlertMessage({
            type: AlertMessageType.ERROR,
            header: t.FORM.OOPS(),
            message: t.FORM.ERROR_SOMETHING_WRONG(),
          }));
        } else if (props.currentLecturePageId === lecturePageId) {
          dispatch(getCourseLecturePageTimeline({
            catalogId: props.catalogId,
            userId,
            lecturePageId,
          })).then(() => {
            /**
             * Calling useLecturePageLink() is the correct method here. But
             * it causes a reload of the content area. So just scrolling to
             * the newly created LC
             */
            const lcElement = document.getElementById(getLectureComponentDomId(result.payload as LectureComponent));
            if (lcElement) {
              setTimeout(() => {
                lcElement.scrollIntoView({ behavior: 'smooth' });
              });
            }
          });
        } else {
          dispatch(addAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: getSuccessMessage(),
            button: redirectButton((result.payload as LectureComponent).id),
            extraStyles: alertMessageExtraStyle,
          }));
        }
      });
    };

    const { models } = store.getState();

    const targetLecturePage = loadLecturePageModel($injector, models.lecturePages[lecturePageId]);

    const moveToWarningMessages: string[] = angularComponent?.moveToWarningMessages(targetLecturePage);

    if (moveToWarningMessages?.length > 0 && source === PreviewSource.MOVE) {
      dispatch(openConfirmationDialog({
        title: t.LECTURE_PAGES.COMPONENTS.MOVE_TITLE(metadata.description(courseAliases)),
        confirmText: t.FORM.YES_SURE(),
        cancelText: t.FORM.CANCEL(),
        onConfirm: doActionTo,
        bodyText: (
          <div className='text-justify'>
            {moveToWarningMessages.map(message => <li>{message}</li>)}
          </div>
        ),
        footerText: t.DASHBOARD.CONFIRMATION.ARE_YOU_SURE(),
        onCancel: () => setIsRequesting(false),
      }));
    } else {
      doActionTo();
    }
  }, [
    source, $injector, lecturePageId, angularComponent, dispatch,
    props.catalogId, props.currentLecturePageId, props.index, component,
    onClosePreviewModal, userId, lectureLink, metadata, courseAliases,
  ]);

  return (
    <div css={styles} className='mx-auto my-2'>
      <ClickableContainer
        className='container-bar'
        onClick={onSelect}
        disabled={isRequesting}
        isFocusable={!isRequesting}
        aria-label={
          isRequesting
            ? t.LECTURE_PAGES.COMPONENTS[source.toUpperCase()].REQUESTING()
            : t.LECTURE_PAGES.COMPONENTS[source.toUpperCase()].INITIAL()
        }
      >
        <div className='copy-bar align-items-center text-primary'>
          <NvIcon icon='duplicate' size='xxs-smallest' className='mr-1' />
          <span className='text-primary text-small'>
            {isRequesting ? t.LECTURE_PAGES.COMPONENTS[source.toUpperCase()].REQUESTING()
              : t.LECTURE_PAGES.COMPONENTS[source.toUpperCase()].INITIAL()}
          </span>
        </div>
      </ClickableContainer>
    </div>
  );
};

export default NvCopyComponent;
